import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMsal, useIsAuthenticated } from "@azure/msal-react"; // Import MSAL hooks
import { loginRequest } from "../auth/msalConfig"; // Import MSAL scopes
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "../components/ui/use-toast";
import axios from "axios";
import { Separator } from "../components/ui/separator"; // Import Separator
import { LogIn } from "lucide-react"; // Import an icon

export default function LoginPage() {
  const { login, handleMicrosoftLoginCallback } = useAuth(); // Get MSAL callback handler
  const { instance } = useMsal(); // Get MSAL instance
  const isAuthenticated = useIsAuthenticated(); // Check MSAL auth state

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [msalLoading, setMsalLoading] = useState(false); // Loading state for MSAL
  const navigate = useNavigate();
  const location = useLocation();

  // Traditional Login Handler
  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      console.log("Attempting traditional login for:", username);
      const res = await axios.post("/login", { username, password });
      const token = res.data.access_token;
      await login({ token }); // Use existing login function
      toast({
        title: "Success",
        description: `Logged in traditionally!`,
      });
      navigate(location.state?.from?.pathname || "/");
    } catch (err) {
      console.error("Traditional Login failed:", err);
      toast({
        title: "Login failed",
        description:
          err.response?.data?.error || err.message || "Check credentials",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  // Microsoft Login Handler
  const handleMicrosoftLogin = async () => {
    setMsalLoading(true);
    try {
      // Always use popup login to avoid hash fragment issues
      console.log("Using popup login flow");
      const popupRequest = {
        ...loginRequest,
        prompt: "select_account", // Always show account selector
        redirectUri: window.location.origin, // Explicitly set redirect URI
        popupWindowAttributes: {
          width: 500,
          height: 600,
          top: (window.screen.height - 600) / 2,
          left: (window.screen.width - 500) / 2,
        }
      };
      const response = await instance.loginPopup(popupRequest);
        console.log("MSAL login response:", response);
        // Set the active account before sending to backend
        if (response.account) {
          instance.setActiveAccount(response.account);
        }
        // Send the ID token to your backend
      await handleMicrosoftLoginCallback(response.idToken, response.account);
      toast({
        title: "Success",
        description: `Logged in with Microsoft as ${response.account?.name || "User"}!`, // Use name from MSAL account
      });
      navigate(location.state?.from?.pathname || "/"); // Redirect after backend confirms
    } catch (error) {
      console.error("Microsoft Login failed:", error);
      // Provide more specific error messages based on error type
      let errorMessage = "Could not log in with Microsoft.";
      
      if (error.errorCode === "popup_window_error" || error.errorCode === "empty_window_error") {
        // Popup was blocked, try redirect flow as fallback
        console.log("Popup blocked, falling back to redirect flow");
        try {
          const redirectRequest = {
            ...loginRequest,
            prompt: "select_account",
            redirectUri: window.location.origin, // Remove trailing slash for hash router
          };
          await instance.loginRedirect(redirectRequest);
          return; // Don't show error, redirect will happen
        } catch (redirectError) {
          console.error("Redirect flow also failed:", redirectError);
          errorMessage = "Both popup and redirect authentication failed. Please check your browser settings.";
        }
      } else if (error.errorCode === "access_denied" || error.message.includes("access_denied")) {
        errorMessage = "You declined to give permission. We'll continue with basic permissions only.";
      } else if (error.name === "ServerError") {
        errorMessage = "Server error during Microsoft login. Please try again later.";
      } else if (error.name === "BrowserAuthError") {
        errorMessage = "Browser authentication error. Please try again in a private window.";
      } else if (error.errorCode === "hash_does_not_contain_known_properties") {
        errorMessage = "Authentication response was corrupted. Please clear your browser cache and try again.";
      }
      toast({
        title: "Microsoft Login Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setMsalLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign In</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Microsoft Login Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handleMicrosoftLogin}
              disabled={msalLoading || loading}
            >
              {msalLoading ? (
                "Processing..."
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" /> Login with Microsoft
                </>
              )}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <Separator />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            {/* Traditional Login Form */}
            <form className="space-y-4" onSubmit={handleSubmit}>
              <Input
                required
                placeholder="Username or Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={msalLoading || loading}
              />
              <Input
                required
                type="password"
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={msalLoading || loading}
              />
              <Button
                type="submit"
                className="w-full"
                disabled={loading || msalLoading}
              >
                {loading ? "Logging in..." : "Login with Password"}
              </Button>
            </form>
          </div>
          <div className="mt-6 text-center">
            <Link className="text-sm text-primary hover:underline" to="/signup">
              Don't have an account? Create one
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
